import {
  BrainCircuit,
  Building2,
  Camera,
  Clapperboard,
  Coins,
  Cpu,
  Film,
  Filter,
  Flag,
  FlaskConical,
  Funnel,
  Images,
  Inbox,
  LayoutDashboard,
  ListOrdered,
  Link2,
  Mail,
  MailOpen,
  Megaphone,
  MessageSquareWarning,
  Music,
  QrCode,
  RadioTower,
  Send,
  Settings,
  Share2,
  Siren,
  Sparkles,
  TestTubes,
  ToggleLeft,
  Users,
  WalletCards,
  type LucideIcon,
} from "lucide-react";
import { matchPath } from "react-router";

/**
 * Single source of truth for every CRM destination. The desktop header, the
 * "More" dropdown and the mobile bottom bar all render from this list, so a new
 * page is added here once and every navigation surface picks it up — and the
 * active-tab resolution is one pure function instead of a per-surface if/else
 * chain.
 */

export type NavLabel =
  | {
      kind: "i18n";
      key: string;
      smartCount?: number;
      /** Shown when the key is missing from the active locale. */
      fallback: string;
    }
  | { kind: "text"; text: string };

export type NavAccess = { resource: string; action: string };

export type NavItem = {
  id: string;
  label: NavLabel;
  to: string;
  /** react-router patterns that mark this item active. */
  match: readonly string[];
  icon: LucideIcon;
  /** ra-core `CanAccess` gate; unset means every signed-in user sees it. */
  access?: NavAccess;
  /** Dynamic count rendered as a pill after the label. */
  badge?: "inbox";
};

export type NavGroup = {
  id: string;
  label: string;
  items: readonly NavItem[];
};

const i18n = (
  key: string,
  fallback: string,
  smartCount?: number,
): NavLabel => ({
  kind: "i18n",
  key,
  smartCount,
  fallback,
});
const text = (value: string): NavLabel => ({ kind: "text", text: value });

const ADMIN_ANALYTICS: NavAccess = {
  resource: "marketing_metrics",
  action: "list",
};
const ADMIN_CONFIGURATION: NavAccess = {
  resource: "configuration",
  action: "edit",
};

export const HOME_NAV_ITEM: NavItem = {
  id: "home",
  label: i18n("crm.navigation.home", "Home"),
  to: "/",
  match: ["/"],
  icon: LayoutDashboard,
};

/** The tabs that stay visible in the header, in order. */
export const PRIMARY_NAV_ITEMS: readonly NavItem[] = [
  {
    id: "contacts",
    label: i18n("resources.contacts.name", "Contacts", 2),
    to: "/contacts",
    match: ["/contacts/*"],
    icon: Users,
  },
  {
    id: "echoes",
    label: i18n("resources.echoflicks_echoes.name", "Echoes", 2),
    to: "/echoflicks_echoes",
    match: ["/echoflicks_echoes/*", "/echoflicks_video_sessions/*"],
    icon: Clapperboard,
  },
  {
    id: "queue",
    label: text("Queue"),
    to: "/queue",
    match: ["/queue/*"],
    icon: ListOrdered,
    access: { resource: "pipeline_queue", action: "list" },
  },
  {
    id: "broadcasts",
    label: i18n("crm.whatsapp.nav", "Broadcasts"),
    to: "/whatsapp_broadcasts",
    match: ["/whatsapp_broadcasts/*", "/whatsapp_templates/*"],
    icon: RadioTower,
  },
  {
    id: "feedback",
    label: text("Feedback"),
    to: "/echo_feedback",
    match: ["/echo_feedback/*"],
    icon: MessageSquareWarning,
    access: { resource: "echo_feedback", action: "list" },
  },
  {
    id: "marketing",
    label: text("Marketing"),
    to: "/marketing",
    match: ["/marketing/*"],
    icon: Megaphone,
    access: ADMIN_ANALYTICS,
  },
  {
    id: "funnel",
    label: text("Funnel"),
    to: "/funnel",
    match: ["/funnel/*"],
    icon: Filter,
    access: ADMIN_ANALYTICS,
  },
];

/** Everything else, grouped for the "More" menu. */
export const MORE_NAV_GROUPS: readonly NavGroup[] = [
  {
    id: "operations",
    label: "Operations",
    items: [
      {
        id: "inbox",
        label: i18n("crm.whatsapp.inbox.nav", "Inbox"),
        to: "/whatsapp_inbox",
        match: ["/whatsapp_inbox/*"],
        icon: Inbox,
        badge: "inbox",
      },
      {
        // Admin-only: approved Echoes that failed, were rejected by WhatsApp
        // or are overdue, and whether each customer got a film in the end.
        id: "incidents",
        label: text("Incidents"),
        to: "/echo-incidents",
        match: ["/echo-incidents/*"],
        icon: Siren,
        access: { resource: "echo_incidents", action: "list" },
      },
      {
        // Admin-only replay of an Echo's recorded creation history.
        id: "echo_studio",
        label: text("Echo Studio"),
        to: "/echo-studio",
        match: ["/echo-studio/*"],
        icon: Film,
        access: { resource: "echo_studio", action: "list" },
      },
      {
        id: "echoflicks_tv",
        label: text("Echoflicks TV"),
        to: "/tv",
        match: ["/tv/*"],
        icon: Film,
      },
      {
        id: "links",
        label: text("Links"),
        to: "/tracking-links",
        match: ["/tracking-links/*"],
        icon: Link2,
      },
      {
        id: "poster_emails",
        label: text("Poster Emails"),
        to: "/echoflicks_poster_emails",
        match: ["/echoflicks_poster_emails/*"],
        icon: Mail,
        access: { resource: "echoflicks_poster_emails", action: "list" },
      },
      {
        id: "subscribers",
        label: i18n("resources.subscribers.name", "Subscribers", 2),
        to: "/subscribers",
        match: ["/subscribers/*"],
        icon: MailOpen,
      },
      {
        id: "tepone_codes",
        label: i18n("resources.tepone_codes.name", "Te Pone codes", 2),
        to: "/tepone_codes",
        match: ["/tepone_codes/*"],
        icon: QrCode,
      },
      {
        id: "guerilla",
        label: text("Guerilla"),
        to: "/guerilla",
        match: ["/guerilla/*"],
        icon: Camera,
      },
    ],
  },
  {
    id: "growth",
    label: "Growth",
    items: [
      {
        id: "referrals",
        label: text("Referrals"),
        to: "/referrals",
        match: ["/referrals/*", "/sharing/*"],
        icon: Share2,
        access: ADMIN_ANALYTICS,
      },
      {
        id: "experiments",
        label: text("Experiments"),
        to: "/experiments",
        match: ["/experiments/*"],
        icon: TestTubes,
        access: ADMIN_ANALYTICS,
      },
      {
        id: "monetization",
        label: text("Monetization"),
        to: "/monetization",
        match: ["/monetization/*"],
        icon: WalletCards,
        access: ADMIN_ANALYTICS,
      },
      {
        id: "costs",
        label: text("Costs"),
        to: "/costs",
        match: ["/costs/*"],
        icon: Coins,
        access: ADMIN_ANALYTICS,
      },
      {
        // my.echoflicks.com adoption: who signs in to their Echo library.
        id: "gallery_usage",
        label: text("Gallery usage"),
        to: "/gallery-usage",
        match: ["/gallery-usage/*"],
        icon: Images,
        access: ADMIN_ANALYTICS,
      },
      {
        id: "cohorts",
        label: i18n("resources.feature_cohorts.name", "Cohorts", 2),
        to: "/feature_cohorts",
        match: ["/feature_cohorts/*"],
        icon: Flag,
        access: { resource: "feature_cohorts", action: "list" },
      },
      {
        // Real-estate self-serve funnel: signup link → agency → first film,
        // per lead, with who is stuck now. Admin analytics (lead e-mails).
        id: "re_funnel",
        label: text("RE funnel"),
        to: "/re-funnel",
        match: ["/re-funnel/*"],
        icon: Funnel,
        access: ADMIN_ANALYTICS,
      },
      {
        id: "agencies",
        label: i18n("resources.agencies.name", "Agencies", 2),
        to: "/agencies",
        match: ["/agencies/*"],
        icon: Building2,
        access: { resource: "agencies", action: "list" },
      },
      {
        // First-film outreach: generate an agency's first film, email the
        // agent from a template, follow opens, clicks and the sign-in.
        id: "outreach",
        label: text("Outreach"),
        to: "/outreach",
        match: ["/outreach/*"],
        icon: Send,
        access: { resource: "outreach", action: "list" },
      },
    ],
  },
  {
    id: "studio",
    label: "Studio & configuration",
    items: [
      {
        id: "sound",
        label: text("Sound"),
        to: "/sound",
        match: ["/sound/*"],
        icon: Music,
        access: ADMIN_CONFIGURATION,
      },
      {
        id: "labs",
        label: text("Labs"),
        to: "/labs",
        match: ["/labs/*"],
        icon: FlaskConical,
        access: ADMIN_CONFIGURATION,
      },
      {
        id: "ai_settings",
        label: text("AI Settings"),
        to: "/ai-settings",
        match: ["/ai-settings/*"],
        icon: Sparkles,
        access: ADMIN_CONFIGURATION,
      },
      {
        id: "gpu_fleet",
        label: text("GPU Fleet"),
        to: "/gpu-fleet",
        match: ["/gpu-fleet/*"],
        icon: Cpu,
        access: ADMIN_CONFIGURATION,
      },
      {
        // Which model does which step, live from the signed runtime config
        // and the newest Echoes' cost ledger. Read-only.
        id: "llm_config",
        label: text("LLM Config"),
        to: "/llm-config",
        match: ["/llm-config/*"],
        icon: BrainCircuit,
        access: ADMIN_CONFIGURATION,
      },
      {
        id: "feature_flags",
        label: text("Feature Flags"),
        to: "/feature-flags",
        match: ["/feature-flags/*"],
        icon: ToggleLeft,
        access: ADMIN_CONFIGURATION,
      },
      {
        id: "lab_results",
        label: i18n("crm.lab_results.nav", "Lab results"),
        to: "/lab-results",
        match: ["/lab-results/*"],
        icon: Images,
      },
    ],
  },
];

/** Mobile-only: settings lives in the bottom bar's "More" sheet. */
export const SETTINGS_NAV_ITEM: NavItem = {
  id: "settings",
  label: i18n("crm.settings.title", "Settings"),
  to: "/settings",
  match: ["/settings/*"],
  icon: Settings,
};

export const MORE_NAV_ITEMS: readonly NavItem[] = MORE_NAV_GROUPS.flatMap(
  (group) => group.items,
);

const ALL_NAV_ITEMS: readonly NavItem[] = [
  HOME_NAV_ITEM,
  ...PRIMARY_NAV_ITEMS,
  ...MORE_NAV_ITEMS,
  SETTINGS_NAV_ITEM,
];

export function isNavItemActive(item: NavItem, pathname: string): boolean {
  return item.match.some((pattern) =>
    pattern === "/"
      ? matchPath({ path: "/", end: true }, pathname) != null
      : matchPath(pattern, pathname) != null,
  );
}

/** Which destination the current URL belongs to, or null for unlisted pages. */
export function resolveActiveNavId(pathname: string): string | null {
  return (
    ALL_NAV_ITEMS.find((item) => isNavItemActive(item, pathname))?.id ?? null
  );
}

/** True when the URL belongs to something tucked under "More". */
export function isMoreNavActive(pathname: string): boolean {
  const active = resolveActiveNavId(pathname);
  return active != null && MORE_NAV_ITEMS.some((item) => item.id === active);
}

type Translate = (
  key: string,
  options?: { smart_count?: number; _?: string },
) => string;

export function navLabel(item: NavItem, translate: Translate): string {
  if (item.label.kind === "text") return item.label.text;
  const { key, smartCount, fallback } = item.label;
  return translate(key, {
    _: fallback,
    ...(smartCount == null ? {} : { smart_count: smartCount }),
  });
}
